<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
  collections: { type: Array, default: () => [] },
});

const emit = defineEmits(['upload', 'close']);

const currentStep = ref(1);

const selectedFile = ref(null);
const collection = ref('');
const newCollection = ref('');
const useNewCollection = ref(false);
const isDragging = ref(false);
const fileError = ref('');

const ALLOWED_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.csv', '.txt'];
const MAX_SIZE = 50 * 1024 * 1024;

const finalCollection = computed(() => {
  if (useNewCollection.value) return newCollection.value.trim();
  return collection.value;
});

const canProceed = computed(() => {
  if (currentStep.value === 1) return selectedFile.value && !fileError.value;
  return true;
});

const canUpload = computed(() => {
  return selectedFile.value && !fileError.value && !props.uploading;
});

const stepLabels = computed(() => [
  t('OMNI.DATA_SOURCES.STEP_UPLOAD'),
  t('OMNI.DATA_SOURCES.STEP_OPTIONS'),
  t('OMNI.DATA_SOURCES.STEP_CONFIRM'),
]);

const fileExtension = computed(() => {
  if (!selectedFile.value) return '';
  return selectedFile.value.name.split('.').pop().toUpperCase();
});

watch(() => props.isOpen, val => {
  if (!val) resetForm();
});

const resetForm = () => {
  currentStep.value = 1;
  selectedFile.value = null;
  collection.value = '';
  newCollection.value = '';
  useNewCollection.value = false;
  fileError.value = '';
  isDragging.value = false;
};

const validateFile = file => {
  fileError.value = '';
  if (!file) return false;
  if (file.size > MAX_SIZE) {
    fileError.value = t('OMNI.DATA_SOURCES.ERROR_FILE_TOO_LARGE', { max: '50MB' });
    return false;
  }
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    fileError.value = t('OMNI.DATA_SOURCES.ERROR_INVALID_TYPE');
    return false;
  }
  return true;
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (file && validateFile(file)) selectedFile.value = file;
  else selectedFile.value = null;
};

const handleDrop = event => {
  event.preventDefault();
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file && validateFile(file)) selectedFile.value = file;
  else selectedFile.value = null;
};

const handleDragOver = event => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => { isDragging.value = false; };

const nextStep = () => {
  if (currentStep.value < 3) currentStep.value++;
};

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--;
};

const handleUpload = () => {
  if (!canUpload.value) return;
  emit('upload', {
    file: selectedFile.value,
    collection: finalCollection.value || null,
  });
};

const handleClose = () => {
  resetForm();
  emit('close');
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[90vh]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('OMNI.DATA_SOURCES.UPLOAD_TITLE') }}
          </h2>
          <button
            class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            @click="handleClose"
          >
            <span class="i-lucide-x w-4 h-4" />
          </button>
        </div>

        <!-- Stepper -->
        <div class="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
          <div class="flex items-center justify-between">
            <template v-for="(label, index) in stepLabels" :key="index">
              <div class="flex items-center gap-2">
                <div
                  class="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all"
                  :class="currentStep > index + 1
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : currentStep === index + 1
                      ? 'bg-woot-500 text-white'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'"
                >
                  <span v-if="currentStep > index + 1" class="i-lucide-check w-3.5 h-3.5" />
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <span
                  class="text-xs font-medium hidden sm:inline"
                  :class="currentStep >= index + 1
                    ? 'text-slate-700 dark:text-slate-300'
                    : 'text-slate-400 dark:text-slate-500'"
                >{{ label }}</span>
              </div>
              <div
                v-if="index < stepLabels.length - 1"
                class="flex-1 h-px mx-2"
                :class="currentStep > index + 1
                  ? 'bg-emerald-200 dark:bg-emerald-800'
                  : 'bg-slate-200 dark:bg-slate-700'"
              />
            </template>
          </div>
        </div>

        <!-- Step Content -->
        <div class="px-6 py-5 overflow-y-auto flex-1">
          <!-- Step 1: Upload -->
          <div v-if="currentStep === 1">
            <div
              class="border-2 border-dashed rounded-xl p-8 text-center transition-all"
              :class="isDragging
                ? 'border-woot-500 bg-woot-50/50 dark:bg-woot-900/10'
                : selectedFile
                  ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-700'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'"
              @drop="handleDrop"
              @dragover="handleDragOver"
              @dragleave="handleDragLeave"
            >
              <div v-if="!selectedFile">
                <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 mx-auto mb-3">
                  <span class="i-lucide-upload-cloud text-2xl text-slate-400 dark:text-slate-500" />
                </div>
                <p class="text-sm text-slate-600 dark:text-slate-400 mb-1">{{ t('OMNI.DATA_SOURCES.DROP_FILE_HERE') }}</p>
                <p class="text-xs text-slate-400 dark:text-slate-500 mb-4">{{ t('OMNI.DATA_SOURCES.SUPPORTED_FORMATS') }}</p>
                <label class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/20 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/30 cursor-pointer transition-colors">
                  <span class="i-lucide-folder-open" />
                  {{ t('OMNI.DATA_SOURCES.BROWSE_FILES') }}
                  <input type="file" class="hidden" accept=".pdf,.xlsx,.xls,.csv,.txt" @change="handleFileSelect" />
                </label>
              </div>
              <div v-else class="flex items-center gap-4">
                <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex-shrink-0">
                  <span class="i-lucide-file-check text-2xl text-emerald-600 dark:text-emerald-400" />
                </div>
                <div class="text-left flex-1 min-w-0">
                  <p class="font-medium text-slate-900 dark:text-slate-100 truncate">{{ selectedFile.name }}</p>
                  <div class="flex items-center gap-2 mt-0.5">
                    <span class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{{ fileExtension }}</span>
                    <span class="text-xs text-slate-400">{{ formatFileSize(selectedFile.size) }}</span>
                  </div>
                </div>
                <button class="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors" @click="selectedFile = null">
                  <span class="i-lucide-x w-4 h-4" />
                </button>
              </div>
            </div>
            <div v-if="fileError" class="mt-3 flex items-center gap-2 text-sm text-red-500">
              <span class="i-lucide-alert-circle w-4 h-4" />
              {{ fileError }}
            </div>
          </div>

          <!-- Step 2: Options (collection only) -->
          <div v-if="currentStep === 2">
            <div class="space-y-5">
              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {{ t('OMNI.DATA_SOURCES.COLLECTION') }}
                  <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.DATA_SOURCES.OPTIONAL') }})</span>
                </label>
                <div class="flex items-center gap-2 mb-3">
                  <button
                    class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                    :class="!useNewCollection ? 'bg-woot-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'"
                    @click="useNewCollection = false"
                  >{{ t('OMNI.DATA_SOURCES.EXISTING_COLLECTION') }}</button>
                  <button
                    class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                    :class="useNewCollection ? 'bg-woot-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'"
                    @click="useNewCollection = true"
                  >{{ t('OMNI.DATA_SOURCES.NEW_COLLECTION') }}</button>
                </div>
                <div v-if="!useNewCollection" class="relative">
                  <select
                    v-model="collection"
                    class="w-full appearance-none bg-none px-3 py-2 pr-9 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  >
                    <option value="">{{ t('OMNI.DATA_SOURCES.AUTO_DETECT') }}</option>
                    <option v-for="col in collections" :key="col" :value="col">{{ col }}</option>
                  </select>
                  <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 w-4 h-4" />
                </div>
                <input
                  v-else
                  v-model="newCollection"
                  type="text"
                  class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  :placeholder="t('OMNI.DATA_SOURCES.NEW_COLLECTION_PLACEHOLDER')"
                />
              </div>

              <div class="p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/20">
                <div class="flex items-start gap-3">
                  <span class="i-lucide-lightbulb text-blue-500 flex-shrink-0 mt-0.5" />
                  <div class="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <p>{{ t('OMNI.DATA_SOURCES.HOW_PROCESSED_1') }}</p>
                    <p>{{ t('OMNI.DATA_SOURCES.HOW_PROCESSED_2') }}</p>
                    <p>{{ t('OMNI.DATA_SOURCES.HOW_PROCESSED_4') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Confirm -->
          <div v-if="currentStep === 3">
            <div class="space-y-4">
              <div class="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700">
                <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{{ t('OMNI.DATA_SOURCES.SUMMARY') }}</h3>
                <div class="space-y-3">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-slate-500">{{ t('OMNI.DATA_SOURCES.SUMMARY_FILE') }}</span>
                    <div class="flex items-center gap-2">
                      <span class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300">{{ fileExtension }}</span>
                      <span class="text-slate-700 dark:text-slate-300 font-medium truncate max-w-[200px]">{{ selectedFile?.name }}</span>
                    </div>
                  </div>
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-slate-500">{{ t('OMNI.DATA_SOURCES.SUMMARY_COLLECTION') }}</span>
                    <span class="text-slate-700 dark:text-slate-300 font-medium">{{ finalCollection || t('OMNI.DATA_SOURCES.AUTO_DETECT') }}</span>
                  </div>
                </div>
              </div>

              <div class="p-4 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-900/20">
                <div class="flex items-start gap-3">
                  <span class="i-lucide-rocket text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p class="text-xs text-emerald-700 dark:text-emerald-300">
                    {{ t('OMNI.DATA_SOURCES.CONFIRM_HINT') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
          <button
            v-if="currentStep > 1"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            @click="prevStep"
          >
            <span class="i-lucide-arrow-left w-3.5 h-3.5" />
            {{ t('OMNI.DATA_SOURCES.BACK') }}
          </button>
          <div v-else class="w-1" />

          <div class="flex gap-2">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              @click="handleClose"
            >{{ t('OMNI.DATA_SOURCES.CANCEL') }}</button>

            <button
              v-if="currentStep < 3"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
              :disabled="!canProceed"
              @click="nextStep"
            >
              {{ t('OMNI.DATA_SOURCES.NEXT') }}
              <span class="i-lucide-arrow-right w-3.5 h-3.5" />
            </button>

            <button
              v-else
              class="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
              :disabled="!canUpload"
              @click="handleUpload"
            >
              <span v-if="uploading" class="i-lucide-loader-2 animate-spin w-3.5 h-3.5" />
              <span v-else class="i-lucide-upload w-3.5 h-3.5" />
              {{ uploading ? t('OMNI.DATA_SOURCES.UPLOADING') : t('OMNI.DATA_SOURCES.CONFIRM_UPLOAD') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
